import { useContext, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Plus, Printer, X } from 'lucide-react';
import Button from '../../components/Button';
import { useThermalPrinter } from '../../context/ThermalPrinterContext';
import { ProductsContext } from '../../context/ProductsContext';
import { formatCurrency } from '../../data/mockData';
import { getPosSale } from '../../api/pos';
import { printReceiptBrowser } from '../../utils/printReceiptBrowser';
import {
  POS_COUNTER, POS_GSTIN, isPosReceiptRevised, isPosReceiptVoided, posReceiptRevisionLabel,
} from '../../utils/escpos';
import {
  calculateBillSavings,
  formatDiscountLabel,
  formatDiscountPercent,
  formatItemSavingsText,
  getItemMrp,
  getItemSavings,
} from '../../utils/posMoney';
import {
  formatItemsQtySummary, formatLineQty, unitPriceLabel,
} from '../../utils/soldBy';
import bhimaMartLogo from '../../assets/bhima mart logo black.svg';
import styles from './Receipt.module.css';

const PAYMENT_LABEL = { CASH: 'Cash', UPI: 'UPI', CARD: 'Card', PAY_LATER: 'Pay Later' };

export default function Receipt({
  receipt: initialReceipt,
  changeDue = 0,
  token,
  mode = 'sale',
  onNewSale,
  onClose,
  closeLabel = 'Close',
  products: propProducts = [],
}) {
  const productsCtx = useContext(ProductsContext);
  const catalogProducts = (propProducts && propProducts.length > 0)
    ? propProducts
    : (productsCtx?.products || []);
  const { connected, printReceipt } = useThermalPrinter();
  const [receipt, setReceipt] = useState(initialReceipt);
  const [reprinting, setReprinting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [reprintError, setReprintError] = useState('');
  const [printNotice, setPrintNotice] = useState('');
  const [printWarning, setPrintWarning] = useState('');
  const [printAttempted, setPrintAttempted] = useState(mode === 'reprint');
  const autoPrintDone = useRef(false);

  const isReprintMode = mode === 'reprint';
  const isConfirmedSale = Boolean(receipt.order_id);
  const isVoided = isPosReceiptVoided(receipt);
  const isRevised = isPosReceiptRevised(receipt);
  const revisionLabel = posReceiptRevisionLabel(receipt);

  useEffect(() => {
    setReceipt(initialReceipt);
  }, [initialReceipt]);

  const dt = new Date(receipt.created_at);
  const dateShort = dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
  const timeShort = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const counter = receipt.counter || POS_COUNTER;
  const gstin = receipt.gstin || POS_GSTIN;
  const cashierName = receipt.cashier_name || '—';
  const customerNumber = receipt.customer_number || '—';
  const customerPhone = receipt.customer_phone || receipt.phone || null;
  const customerName = receipt.customer_name || null;
  const isPayLater = String(receipt.payment_method || '').toUpperCase() === 'PAY_LATER';
  const payLaterStatus = String(receipt.payment_status || '').toUpperCase();
  const payLaterCollected = isPayLater && payLaterStatus === 'COLLECTED';
  const outstanding = receipt.outstanding != null ? Number(receipt.outstanding) : Number(receipt.total) || 0;
  const settlementMethod = receipt.settlement_method || null;
  const settlementRoundOff = Number(receipt.settlement_round_off) || 0;
  const addressLine1 = receipt.address_line1 || null;
  const addressLine2 = receipt.address_line2 || null;
  const block = receipt.block || null;
  const flat = receipt.flat || null;
  const roundOff = Number(receipt.round_off) || 0;
  const taxedSlabs = (receipt.gst_breakup || []).filter((g) => g.rate > 0);
  const totalGst = taxedSlabs.reduce((sum, g) => sum + g.tax, 0);
  const formatRoundOff = (amount) => {
    const n = Number(amount) || 0;
    if (n > 0) return `+${formatCurrency(n)}`;
    return formatCurrency(n);
  };
  const lineItems = receipt.line_items || [];
  const itemsQtySummary = formatItemsQtySummary(lineItems);
  const billSavings = calculateBillSavings(receipt, catalogProducts);
  const printFrameRef = useRef(null);

  const printViaBrowser = () => {
    printFrameRef.current?.remove();
    printFrameRef.current = printReceiptBrowser(document.getElementById('pos-receipt'));
  };

  useEffect(() => {
    const cleanupPrintFrame = () => {
      printFrameRef.current?.remove();
      printFrameRef.current = null;
    };

    window.addEventListener('afterprint', cleanupPrintFrame);
    return () => {
      window.removeEventListener('afterprint', cleanupPrintFrame);
      cleanupPrintFrame();
    };
  }, []);

  const runPrint = async (nextReceipt) => {
    setPrinting(true);
    setPrintNotice('');
    setPrintWarning('');
    setPrintAttempted(true);

    if (connected) {
      try {
        await printReceipt(nextReceipt, {
          changeDue,
          logoSrc: bhimaMartLogo,
          products: catalogProducts,
        });
        setPrintNotice('Sent to thermal printer.');
        return;
      } catch {
        setPrintWarning('Thermal print failed — opening system print dialog.');
        printViaBrowser();
        return;
      } finally {
        setPrinting(false);
      }
    }

    printViaBrowser();
    setPrinting(false);
  };

  useEffect(() => {
    if (isReprintMode || !connected || autoPrintDone.current) return;
    autoPrintDone.current = true;
    runPrint(receipt);
    // Only auto-print once when the receipt opens with a connected printer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, isReprintMode]);

  const handlePrint = () => runPrint(receipt);

  const handleReprint = async () => {
    if (!receipt.order_id || reprinting) return;
    setReprinting(true);
    setReprintError('');
    setPrintNotice('');
    setPrintWarning('');
    try {
      const data = await getPosSale(receipt.order_id, token);
      const nextReceipt = data.receipt || data;
      setReceipt(nextReceipt);
      await runPrint(nextReceipt);
    } catch (err) {
      setReprintError(err.message || 'Could not reprint this receipt.');
    } finally {
      setReprinting(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <div className={styles.confirmHead}>
          <span className={styles.checkIcon}><CheckCircle2 size={22} /></span>
          <div>
            <p className={styles.confirmTitleRow}>
              <span className={styles.confirmTitle}>
                {isReprintMode
                  ? (isVoided ? 'Reprint voided receipt' : isRevised ? 'Reprint revised receipt' : 'Reprint receipt')
                  : (isVoided ? 'Bill voided' : isRevised ? 'Bill revised' : 'Sale complete')}
              </span>
              {isVoided && <span className={styles.voidedBadge}>Voided</span>}
              {!isVoided && isRevised && (
                <span className={styles.revisedBadge}>
                  Revised{revisionLabel ? ` · r${revisionLabel}` : ''}
                </span>
              )}
            </p>
            <p className={styles.confirmSub}>
              {receipt.display_id}
              {isRevised && revisionLabel ? ` · rev ${revisionLabel}` : ''}
              {' · '}
              {formatCurrency(receipt.total)}
            </p>
          </div>
        </div>

        <div className={styles.receipt} id="pos-receipt">
          <div className={styles.receiptHead}>
            <img src={bhimaMartLogo} alt="Bhima Mart" className={styles.storeLogo} />
            <p className={styles.storeMeta}>Praneeth Pranav Jaitra, ClubHouse Ground Floor</p>
            <p className={styles.storeMeta}>GSTIN: {gstin}</p>
            {isVoided && <p className={styles.voidedBanner}>VOID</p>}
            {!isVoided && isRevised && (
              <>
                <p className={styles.revisedBanner}>REVISED</p>
                {revisionLabel && (
                  <p className={styles.revisedRevision}>Revision {revisionLabel}</p>
                )}
              </>
            )}
            <p className={styles.billTitle}>Bill</p>
          </div>

          <div className={styles.divider} />

          <div className={styles.billMeta}>
            <span className={styles.billMetaItem}>Counter: {counter}</span>
            <span className={styles.billMetaItem}>Bill.no: {receipt.display_id}</span>
            {isRevised && revisionLabel && (
              <span className={styles.billMetaItem}>Rev: {revisionLabel}</span>
            )}
            {isVoided && receipt.void_reason && (
              <span className={styles.billMetaWide}>Void reason: {receipt.void_reason}</span>
            )}
            <span className={styles.billMetaItem}>Date: {dateShort} {timeShort}</span>
            <span className={styles.billMetaItem}>User: {cashierName}</span>
            <span className={styles.billMetaWide}>Customer No: {customerNumber}</span>
            {customerName && (
              <span className={styles.billMetaWide}>Name: {customerName}</span>
            )}
            {customerPhone && (
              <span className={styles.billMetaWide}>Phone: {customerPhone}</span>
            )}
            {isPayLater && (addressLine1 || addressLine2 || block || flat) && (
              <span className={styles.billMetaWide}>
                Address: {[addressLine1, addressLine2, block && `Blk ${block}`, flat && `Flat ${flat}`].filter(Boolean).join(', ')}
              </span>
            )}
          </div>

          <div className={styles.divider} />

          <div className={styles.itemsHead}>
            <span>Item</span>
            <span className={styles.center}>Qty</span>
            <span className={styles.right}>Amount</span>
          </div>
          {lineItems.map((it) => {
            const mrp = getItemMrp(it, catalogProducts);
            const savings = getItemSavings(it, mrp);
            const savingsText = formatItemSavingsText(savings, it, formatCurrency);

            return (
              <div key={`${it.item_id}-${it.sku}-${it.qty}`} className={`${styles.itemRow} ${styles.lineItemRow}`}>
                <span className={styles.itemName}>
                  {it.name}
                  <span className={styles.itemUnit}>
                    {unitPriceLabel(it, formatCurrency)}
                    {savingsText && (
                      <span className={styles.itemSavingsText}>
                        {' · '}{savingsText}
                      </span>
                    )}
                  </span>
                </span>
                <span className={styles.center}>{formatLineQty(it)}</span>
                <span className={styles.right}>{formatCurrency(it.line_total)}</span>
              </div>
            );
          })}

          <div className={styles.divider} />

          <div className={styles.sumRow}><span>Total Items/Qty</span><span>{itemsQtySummary}</span></div>
          <div className={styles.sumRow}><span>Subtotal</span><span>{formatCurrency(receipt.subtotal)}</span></div>
          {(receipt.discount_amount ?? 0) > 0 && (
            <div className={styles.sumRow}>
              <span>{formatDiscountLabel(receipt)}</span>
              <span>−{formatCurrency(receipt.discount_amount)}</span>
            </div>
          )}
          {roundOff !== 0 && (
            <div className={styles.sumRow}>
              <span>Round off</span>
              <span>{formatRoundOff(roundOff)}</span>
            </div>
          )}
          <div className={`${styles.sumRow} ${styles.sumTotal}`}>
            <span>To pay</span>
            <span>{formatCurrency(receipt.total)}</span>
          </div>
          {billSavings.totalSavings > 0 && (
            <div className={`${styles.sumRow} ${styles.sumSavings}`}>
              <span>Total Savings</span>
              <span>
                {formatCurrency(billSavings.totalSavings)}
                {billSavings.totalSavingsPct > 0 ? ` (${formatDiscountPercent(billSavings.totalSavingsPct)})` : ''}
              </span>
            </div>
          )}
          <p className={styles.gstInclusive}>{receipt.note || '(Inclusive of GST)'}</p>

          {taxedSlabs.length > 0 && (
            <>
              <div className={styles.divider} />
              <div className={styles.itemsHead}>
                <span>GST</span>
                <span className={styles.center}>CGST</span>
                <span className={styles.right}>SGST</span>
              </div>
              {taxedSlabs.map((g) => (
                <div key={g.rate} className={styles.itemRow}>
                  <span className={styles.itemName}>
                    {g.rate}%
                    <span className={styles.itemUnit}>on {formatCurrency(g.taxable)}</span>
                  </span>
                  <span className={styles.center}>{formatCurrency(g.cgst)}</span>
                  <span className={styles.right}>{formatCurrency(g.sgst)}</span>
                </div>
              ))}
              <div className={styles.sumRow}><span>Total GST</span><span>{formatCurrency(totalGst)}</span></div>
            </>
          )}

          <div className={styles.divider} />

          <div className={styles.sumRow}>
            <span>
              {isPayLater
                ? (payLaterCollected
                  ? `PAY LATER — paid ${PAYMENT_LABEL[settlementMethod] || settlementMethod || ''}`
                  : 'PAY LATER')
                : `Paid via ${PAYMENT_LABEL[receipt.payment_method] || receipt.payment_method}`}
            </span>
            <span>{formatCurrency(receipt.total)}</span>
          </div>
          {isPayLater && !payLaterCollected && (
            <div className={styles.sumRow}>
              <span>Amount due</span>
              <span>{formatCurrency(outstanding)}</span>
            </div>
          )}
          {isPayLater && payLaterCollected && settlementRoundOff !== 0 && (
            <div className={styles.sumRow}>
              <span>Settlement round off</span>
              <span>{formatRoundOff(settlementRoundOff)}</span>
            </div>
          )}
          {changeDue > 0 && (
            <div className={styles.sumRow}><span>Change</span><span>{formatCurrency(changeDue)}</span></div>
          )}

          {billSavings.totalSavings > 0 && (
            <p className={styles.savingsCallout}>
              You saved {formatCurrency(billSavings.totalSavings)} on this order!
            </p>
          )}

          <p className={styles.feedbackNote}>Give Us feedback on bhimasupermarkets@svyminds.com</p>
          <p className={styles.thankYou}>Thank you for shopping with us!</p>
        </div>

        {printNotice && <p className={styles.printNotice}>{printNotice}</p>}
        {printWarning && <p className={styles.printWarning}>{printWarning}</p>}
        {reprintError && <p className={styles.reprintError}>{reprintError}</p>}

        <div className={styles.actionsFooter}>
          {isReprintMode ? (
            <>
              <Button
                variant="outline"
                className={styles.cancelBtn}
                icon={Printer}
                onClick={handleReprint}
                disabled={reprinting || printing}
              >
                {reprinting || printing ? 'Printing…' : 'Print receipt'}
              </Button>
              <div className={styles.actions}>
                <Button variant="outline" onClick={onClose}>
                  {closeLabel}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                className={styles.cancelBtn}
                icon={Printer}
                onClick={handlePrint}
                disabled={printing}
              >
                {printing ? 'Printing…' : connected ? 'Print again' : 'Print receipt'}
              </Button>
              <div className={styles.actions}>
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button icon={Plus} onClick={onNewSale || onClose}>
                  Start new sale
                </Button>
              </div>
            </>
          )}
        </div>

        {!isReprintMode && isConfirmedSale && printAttempted && !connected && (
          <p className={styles.reprintHint}>
            Print didn&apos;t work?{' '}
            <button type="button" className={styles.reprintLink} onClick={handleReprint} disabled={reprinting}>
              {reprinting ? 'Fetching…' : 'Reprint from server'}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
